import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Heart, MessageCircle, User, Sparkles, Users, Filter, Zap, Bell, X, EyeOff, Trash2, Ban, AlertTriangle, Star, MoreVertical, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import SwipeProfiles from '../components/SwipeProfiles';
import LikesReceived from '../components/LikesReceived';
import DatePlanningAssistant from '../components/DatePlanningAssistant';
import { getDisplayName } from '../utils/displayName';
import { resolveImageUrl } from '../utils/imageUrl';

const Matches = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [activeMatches, setActiveMatches] = useState([]);
  const [likesCount, setLikesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.defaultTab || 'likes'); // Default: 'likes', can be overridden via navigation state
  const [connecting, setConnecting] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [swipedMatch, setSwipedMatch] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [matchToBlock, setMatchToBlock] = useState(null);
  const [lastTap, setLastTap] = useState({ time: 0, matchId: null });
  const [favoritedMatches, setFavoritedMatches] = useState([]);
  const [openMenuMatchId, setOpenMenuMatchId] = useState(null);
  const [showDatePlanner, setShowDatePlanner] = useState(false);
  const [datePlannerMatch, setDatePlannerMatch] = useState(null);

  useEffect(() => {
    fetchMatchData();
    // Load favorites from localStorage
    const savedFavorites = localStorage.getItem('favoritedMatches');
    if (savedFavorites) {
      try {
        setFavoritedMatches(JSON.parse(savedFavorites));
      } catch (e) {
        console.error('Error loading favorites:', e);
      }
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('favoritedMatches', JSON.stringify(favoritedMatches));
  }, [favoritedMatches]);

  const fetchMatchData = async () => {
    try {
      const [suggestionsRes, matchesRes, likesRes] = await Promise.all([
        axios.get('/matches/suggestions'),
        axios.get('/matches'),
        axios.get('/matches/likes-received')
      ]);
      setMatchSuggestions(suggestionsRes.data);
      setActiveMatches(matchesRes.data);
      setLikesCount(likesRes.data.length);
    } catch (error) {
      console.error('Error fetching match data:', error);
      toast.error('Failed to load match data');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (userId) => {
    setConnecting(userId);
    try {
      await axios.post(`/matches/create/${userId}`);
      toast.success('Match request sent! 💕');
      fetchMatchData(); // Refresh data
    } catch (error) {
      console.error('Error creating match:', error);
      toast.error('Failed to send match request');
    } finally {
      setConnecting(null);
    }
  };

  const handleHideUser = async (matchId, userName) => {
    try {
      // TODO: Implement hide user API endpoint
      toast.success(`${userName} has been hidden`);
      setActiveMatches(prev => prev.filter(m => m.id !== matchId));
      setSwipedMatch(null);
    } catch (error) {
      console.error('Error hiding user:', error);
      toast.error('Failed to hide user');
    }
  };

  const handleDeleteMatch = async () => {
    if (!matchToDelete) return;
    try {
      await axios.delete(`/matches/${matchToDelete.id}`);
      toast.success('Match deleted successfully');
      setActiveMatches(prev => prev.filter(m => m.id !== matchToDelete.id));
      setShowDeleteModal(false);
      setMatchToDelete(null);
      setSwipedMatch(null);
    } catch (error) {
      console.error('Error deleting match:', error);
      toast.error('Failed to delete match');
      setShowDeleteModal(false);
    }
  };

  const handleBlockUser = async () => {
    if (!matchToBlock) return;
    try {
      // TODO: Implement block user API endpoint
      await axios.post(`/users/block/${matchToBlock.user.id}`);
      toast.success(`${getDisplayName(matchToBlock.user.full_name)} has been blocked`);
      setActiveMatches(prev => prev.filter(m => m.id !== matchToBlock.id));
      setShowBlockModal(false);
      setMatchToBlock(null);
      setSwipedMatch(null);
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
      setShowBlockModal(false);
    }
  };

  const handleDoubleTap = (matchId, event) => {
    // Prevent if clicking on a button
    if (event?.target?.closest('button[data-action-button]')) {
      return;
    }

    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // milliseconds

    if (
      lastTap.matchId === matchId && 
      now - lastTap.time < DOUBLE_TAP_DELAY
    ) {
      // Double tap detected! Toggle the actions
      if (swipedMatch === matchId) {
        // Close if already open
        setSwipedMatch(null);
      } else {
        // Open if closed
        setSwipedMatch(matchId);
      }
      setLastTap({ time: 0, matchId: null });
    } else {
      // First tap
      setLastTap({ time: now, matchId });
    }
  };

  const handleToggleFavorite = (matchId, userName) => {
    const isFavorited = favoritedMatches.includes(matchId);
    
    if (isFavorited) {
      // Remove from favorites
      setFavoritedMatches(prev => prev.filter(id => id !== matchId));
      toast.success(`${userName} removed from favorites`);
    } else {
      // Add to favorites
      setFavoritedMatches(prev => [...prev, matchId]);
      toast.success(`${userName} added to favorites ⭐`);
    }
    setSwipedMatch(null);
  };

  const handleStartConversation = async (userId, userName) => {
    try {
      // Navigate to messages with specific user
      navigate('/messages', { 
        state: { 
          startConversationWith: {
            id: userId,
            full_name: userName,
            username: userName.toLowerCase().replace(' ', '')
          }
        }
      });
      toast.success(`Opening conversation with ${userName}...`, { duration: 1500 });
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const getCompatibilityColor = (score) => {
    if (score >= 90) return 'text-emerald-700 bg-emerald-100';
    if (score >= 80) return 'text-emerald-600 bg-emerald-50';
    if (score >= 70) return 'text-amber-700 bg-amber-100';
    return 'text-orange-700 bg-orange-100';
  };

  const getCompatibilityText = (score) => {
    if (score >= 90) return 'Exceptional Match';
    if (score >= 80) return 'Great Compatibility';
    if (score >= 70) return 'Good Match';
    return 'Potential Connection';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="matches-loading">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-emerald-700 font-medium">Finding your perfect matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-6 pb-12" data-testid="matches-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-4">Mindful Connections</h1>
          <p className="text-lg text-emerald-600 max-w-2xl mx-auto">
            Discover meaningful connections with people who share your wellness journey and values.
          </p>
        </div>

        {/* Tabs - Gradient Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8 max-w-6xl mx-auto px-4">
          <button
            onClick={() => setActiveTab('likes')}
            className={`group relative overflow-hidden rounded-2xl p-4 transition-all shadow-lg hover:shadow-xl hover:scale-105 ${
              activeTab === 'likes'
                ? 'bg-gradient-to-br from-blue-500 to-indigo-500 text-white scale-[1.02]'
                : 'bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
            data-testid="likes-tab"
          >
            {activeTab === 'likes' && (
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            )}
            <div className="flex flex-col items-center space-y-2 text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeTab === 'likes' ? 'bg-white/20' : 'bg-gradient-to-br from-blue-500 to-indigo-500'
              }`}>
                <Bell className={`w-6 h-6 ${activeTab === 'likes' ? 'text-white' : 'text-white'}`} strokeWidth={2} />
              </div>
              <span className="font-semibold text-sm">Likes</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === 'likes' ? 'bg-white/20' : 'bg-blue-100 text-blue-600'
              }`}>{likesCount}</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('matches')}
            className={`group relative overflow-hidden rounded-2xl p-4 transition-all shadow-lg hover:shadow-xl hover:scale-105 ${
              activeTab === 'matches'
                ? 'bg-gradient-to-br from-pink-500 to-rose-500 text-white scale-[1.02]'
                : 'bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
            data-testid="active-matches-tab"
          >
            {activeTab === 'matches' && (
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            )}
            <div className="flex flex-col items-center space-y-2 text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeTab === 'matches' ? 'bg-white/20' : 'bg-gradient-to-br from-pink-500 to-rose-500'
              }`}>
                <Heart className={`w-6 h-6 ${activeTab === 'matches' ? 'text-white' : 'text-white'}`} strokeWidth={2} />
              </div>
              <span className="font-semibold text-sm">Matches</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === 'matches' ? 'bg-white/20' : 'bg-pink-100 text-pink-600'
              }`}>{activeMatches.length}</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('favorites')}
            className={`group relative overflow-hidden rounded-2xl p-4 transition-all shadow-lg hover:shadow-xl hover:scale-105 ${
              activeTab === 'favorites'
                ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white scale-[1.02]'
                : 'bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
            data-testid="favorites-tab"
          >
            {activeTab === 'favorites' && (
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            )}
            <div className="flex flex-col items-center space-y-2 text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeTab === 'favorites' ? 'bg-white/20' : 'bg-gradient-to-br from-amber-500 to-orange-500'
              }`}>
                <Star className={`w-6 h-6 ${activeTab === 'favorites' ? 'text-white' : 'text-white'}`} strokeWidth={2} />
              </div>
              <span className="font-semibold text-sm">Favorites</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === 'favorites' ? 'bg-white/20' : 'bg-amber-100 text-amber-600'
              }`}>{favoritedMatches.length}</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('suggestions')}
            className={`group relative overflow-hidden rounded-2xl p-4 transition-all shadow-lg hover:shadow-xl hover:scale-105 ${
              activeTab === 'suggestions'
                ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white scale-[1.02]'
                : 'bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
            data-testid="suggestions-tab"
          >
            {activeTab === 'suggestions' && (
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            )}
            <div className="flex flex-col items-center space-y-2 text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeTab === 'suggestions' ? 'bg-white/20' : 'bg-gradient-to-br from-purple-500 to-pink-500'
              }`}>
                <Sparkles className={`w-6 h-6 ${activeTab === 'suggestions' ? 'text-white' : 'text-white'}`} strokeWidth={2} />
              </div>
              <span className="font-semibold text-sm">Discover</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === 'suggestions' ? 'bg-white/20' : 'bg-purple-100 text-purple-600'
              }`}>{matchSuggestions.length}</span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('swipe')}
            className={`group relative overflow-hidden rounded-2xl p-4 transition-all shadow-lg hover:shadow-xl hover:scale-105 ${
              activeTab === 'swipe'
                ? 'bg-gradient-to-br from-emerald-500 to-green-500 text-white scale-[1.02]'
                : 'bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
            data-testid="swipe-tab"
          >
            {activeTab === 'swipe' && (
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            )}
            <div className="flex flex-col items-center space-y-2 text-center">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                activeTab === 'swipe' ? 'bg-white/20' : 'bg-gradient-to-br from-emerald-500 to-green-500'
              }`}>
                <Zap className={`w-6 h-6 ${activeTab === 'swipe' ? 'text-white' : 'text-white'}`} strokeWidth={2} />
              </div>
              <span className="font-semibold text-sm">Swipe</span>
            </div>
          </button>
        </div>

        {/* Swipe Tab */}
        {activeTab === 'swipe' && (
          <div className="animate-fade-in-up">
            <SwipeProfiles user={user} />
          </div>
        )}

        {/* Likes Tab */}
        {activeTab === 'likes' && (
          <div className="animate-fade-in-up">
            <LikesReceived user={user} />
          </div>
        )}

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <div className="animate-fade-in-up">
            {activeMatches.filter(match => favoritedMatches.includes(match.id)).length > 0 ? (
              <>
                {/* Helper Text */}
                <div className="mb-6 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full flex items-center justify-center">
                      <Star className="w-5 h-5 text-white fill-current" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-yellow-800 mb-1">Your Favorite Matches</p>
                      <p className="text-xs text-yellow-600">
                        These are the matches you've marked as favorites for easy access
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activeMatches.filter(match => favoritedMatches.includes(match.id)).map((match, index) => {
                    const isSwiped = swipedMatch === match.id;
                    return (
                      <div 
                        key={match.id} 
                        className={`card animate-fade-in-up stagger-${(index % 3) + 1} relative overflow-hidden transition-transform duration-200 ${isSwiped ? '-translate-x-24' : ''}`}
                        data-testid={`favorite-match-${match.id}`}
                        onClick={(e) => handleDoubleTap(match.id, e)}
                        onTouchStart={(e) => {
                          const touch = e.touches[0];
                          setTouchStart({ x: touch.clientX, matchId: match.id, startTime: Date.now() });
                        }}
                        onTouchMove={(e) => {
                          if (!touchStart || touchStart.matchId !== match.id) return;
                          const touch = e.touches[0];
                          const deltaX = touch.clientX - touchStart.x;
                          const moved = Math.abs(deltaX) > 10;
                          
                          if (moved && deltaX < -50) {
                            setSwipedMatch(match.id);
                            setTouchStart(null);
                          }
                        }}
                        onTouchEnd={(e) => {
                          if (touchStart && touchStart.matchId === match.id) {
                            const duration = Date.now() - touchStart.startTime;
                            const moved = Math.abs(e.changedTouches[0].clientX - touchStart.x) < 10;
                            
                            if (duration < 300 && moved) {
                              handleDoubleTap(match.id, e);
                            }
                          }
                          setTouchStart(null);
                        }}
                        onMouseDown={(e) => {
                          if (e.target.closest('button')) return;
                          setTouchStart({ x: e.clientX, matchId: match.id });
                        }}
                        onMouseMove={(e) => {
                          if (!touchStart || touchStart.matchId !== match.id) return;
                          const deltaX = e.clientX - touchStart.x;
                          if (deltaX < -50) {
                            setSwipedMatch(match.id);
                            setTouchStart(null);
                          }
                        }}
                        onMouseUp={() => {
                          setTouchStart(null);
                        }}
                        onMouseLeave={() => {
                          setTouchStart(null);
                        }}
                      >
                        {/* Favorite Badge */}
                        <div className="absolute top-2 right-2 z-10 bg-yellow-500 text-white rounded-full p-2 shadow-lg">
                          <Star className="w-4 h-4 fill-current" />
                        </div>

                        {/* User Profile Header */}
                        <div className="flex items-center space-x-4 mb-4">
                          {match.user.profile_image ? (
                            <img
                              src={resolveImageUrl(match.user.profile_image)}
                              alt={getDisplayName(match.user.full_name)}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-xl">
                                {match.user.full_name.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-semibold text-emerald-800 dark:text-emerald-100 text-lg">
                                {getDisplayName(match.user.full_name)}
                              </h3>
                            </div>
                            <p className="text-sm text-emerald-600 dark:text-emerald-300">
                              @{match.user.username}
                            </p>
                          </div>
                        </div>

                        {/* Bio */}
                        {match.user.bio && (
                          <p className="text-sm text-emerald-700 dark:text-emerald-200 mb-4 line-clamp-2">
                            {match.user.bio}
                          </p>
                        )}

                        {/* Match Date */}
                        <div className="mt-3 text-xs text-emerald-500 dark:text-emerald-400 text-center">
                          Matched {new Date(match.created_at).toLocaleDateString('en-AU', { 
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-2 mt-4">
                          <button 
                            data-action-button="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(match.id, match.user.full_name);
                            }}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700"
                          >
                            <Star className="w-4 h-4 fill-current" />
                            <span>Remove from Favorites</span>
                          </button>
                          
                          <button 
                            data-action-button="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartConversation(match.user.id, match.user.full_name);
                            }}
                            className="btn-primary w-full flex items-center justify-center space-x-2"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>Send Message</span>
                          </button>
                          
                          <button 
                            data-action-button="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProfile(match);
                            }}
                            className="btn-secondary w-full flex items-center justify-center space-x-2"
                          >
                            <User className="w-4 h-4" />
                            <span>View Profile</span>
                          </button>

                          <button 
                            data-action-button="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDatePlannerMatch(match.user);
                              setShowDatePlanner(true);
                            }}
                            className="w-full flex items-center justify-center space-x-2 py-2 px-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 transition-colors text-sm font-medium"
                            data-testid="plan-date-match-btn"
                          >
                            <Calendar className="w-4 h-4" />
                            <span>Plan a Date</span>
                          </button>
                        </div>

                        {/* Swipe Actions (same as Active Matches) */}
                        {isSwiped && (
                          <div className="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-center space-y-2 pr-2 z-10 animate-fade-in">
                            <div className="bg-white dark:bg-slate-700 rounded-lg px-2 py-1 mb-1 shadow-md">
                              <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Actions</p>
                            </div>
                            
                            <button
                              data-action-button="true"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleHideUser(match.id, match.user.full_name);
                              }}
                              className="p-3 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full shadow-lg hover:from-amber-600 hover:to-amber-700 transition-all transform hover:scale-110"
                              title="Hide User"
                            >
                              <EyeOff className="w-5 h-5" />
                            </button>
                            
                            <button
                              data-action-button="true"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMatchToDelete(match);
                                setShowDeleteModal(true);
                              }}
                              className="p-3 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-110"
                              title="Delete Match"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                            
                            <button
                              data-action-button="true"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMatchToBlock(match);
                                setShowBlockModal(true);
                              }}
                              className="p-3 bg-gradient-to-br from-gray-700 to-gray-800 text-white rounded-full shadow-lg hover:from-gray-800 hover:to-gray-900 transition-all transform hover:scale-110"
                              title="Block User"
                            >
                              <Ban className="w-5 h-5" />
                            </button>
                          </div>
                        )}

                        {/* Close swipe overlay */}
                        {isSwiped && (
                          <div 
                            className="fixed inset-0 z-0"
                            onClick={() => setSwipedMatch(null)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-12 h-12 text-yellow-500 dark:text-yellow-400" />
                </div>
                <h3 className="text-xl font-semibold text-emerald-800 dark:text-emerald-100 mb-2">
                  No Favorites Yet
                </h3>
                <p className="text-emerald-600 dark:text-emerald-300 mb-6 max-w-md mx-auto">
                  Mark your important matches as favorites for quick access. Click the star button on any match card.
                </p>
                <button 
                  onClick={() => setActiveTab('matches')}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <Heart className="w-4 h-4" />
                  <span>View Active Matches</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Suggestions Tab */}
        {activeTab === 'suggestions' && (
          <div className="animate-fade-in-up">
            {matchSuggestions.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {matchSuggestions.map((suggestion, index) => (
                  <div key={suggestion.user.id} className={`match-card animate-fade-in-up stagger-${(index % 3) + 1}`} data-testid={`match-suggestion-${suggestion.user.id}`}>
                    {/* Profile Image */}
                    <div className="relative">
                      <div className="w-full h-48 bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                        {suggestion.user.profile_image ? (
                          <img
                            src={resolveImageUrl(suggestion.user.profile_image)}
                            alt={getDisplayName(suggestion.user.full_name)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="w-16 h-16 text-emerald-400" />
                        )}
                      </div>
                      
                      {/* Compatibility Badge */}
                      <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold ${
                        getCompatibilityColor(suggestion.compatibility_score)
                      }`}>
                        {Math.round(suggestion.compatibility_score)}%
                      </div>
                    </div>

                    {/* Profile Info */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-emerald-800">
                          {getDisplayName(suggestion.user.full_name)}, {suggestion.user.age}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          getCompatibilityColor(suggestion.compatibility_score)
                        }`}>
                          {getCompatibilityText(suggestion.compatibility_score)}
                        </span>
                      </div>
                      
                      <p className="text-emerald-600 text-sm mb-4 line-clamp-2">
                        📍 {suggestion.user.location}
                      </p>
                      
                      {suggestion.user.bio && (
                        <p className="text-emerald-700 text-sm mb-4 line-clamp-3">
                          {suggestion.user.bio}
                        </p>
                      )}

                      {/* Match Reasons */}
                      {suggestion.match_reasons && suggestion.match_reasons.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-emerald-800 mb-2">Why you're compatible:</h4>
                          <div className="space-y-1">
                            {suggestion.match_reasons.slice(0, 2).map((reason, idx) => (
                              <div key={idx} className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                                <span className="text-xs text-emerald-600">{reason}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Mental Health Goals */}
                      {suggestion.user.mental_health_goals && suggestion.user.mental_health_goals.length > 0 && (
                        <div className="mb-4">
                          <div className="flex flex-wrap gap-1">
                            {suggestion.user.mental_health_goals.slice(0, 3).map(goal => (
                              <span key={goal} className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                {goal}
                              </span>
                            ))}
                            {suggestion.user.mental_health_goals.length > 3 && (
                              <span className="px-2 py-1 bg-emerald-50 text-emerald-600 text-xs rounded-full">
                                +{suggestion.user.mental_health_goals.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Connect Button */}
                      <button
                        onClick={() => handleConnect(suggestion.user.id)}
                        disabled={connecting === suggestion.user.id}
                        className="btn-primary w-full py-2 flex items-center justify-center space-x-2 disabled:opacity-50"
                        data-testid={`connect-btn-${suggestion.user.id}`}
                      >
                        {connecting === suggestion.user.id ? (
                          <div className="spinner border-white"></div>
                        ) : (
                          <>
                            <Heart className="w-4 h-4" />
                            <span>Connect</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="card max-w-md mx-auto">
                  <Sparkles className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-emerald-800 mb-2">Finding Your Perfect Matches</h3>
                  <p className="text-emerald-600 mb-6">
                    Complete your profile and start tracking your mood to get better match suggestions!
                  </p>
                  <div className="flex flex-col space-y-2">
                    <a href="/profile" className="btn-primary">
                      Complete Profile
                    </a>
                    <a href="/mood" className="btn-secondary">
                      Track Your Mood
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active Matches Tab */}
        {activeTab === 'matches' && (
          <div className="animate-fade-in-up">
            {activeMatches.length > 0 ? (
              <>
                {/* Helper Text */}
                <div className="mb-6 bg-gradient-to-r from-emerald-50 via-green-50 to-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">💡</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-emerald-800 mb-1">Quick Actions</p>
                      <p className="text-xs text-emerald-600">
                        <strong>Double-tap</strong> or <strong>swipe left</strong> to show/hide actions • 
                        <strong className="ml-1">Tap outside</strong> to close
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeMatches.map((match, index) => {
                  const isSwiped = swipedMatch === match.id;
                  return (
                  <div 
                    key={match.id} 
                    className={`card animate-fade-in-up stagger-${(index % 3) + 1} relative overflow-hidden transition-transform duration-200 ${isSwiped ? '-translate-x-24' : ''}`} 
                    data-testid={`active-match-${match.id}`}
                    onClick={(e) => handleDoubleTap(match.id, e)}
                    onTouchStart={(e) => {
                      const touch = e.touches[0];
                      setTouchStart({ x: touch.clientX, matchId: match.id, startTime: Date.now() });
                    }}
                    onTouchMove={(e) => {
                      if (!touchStart || touchStart.matchId !== match.id) return;
                      const touch = e.touches[0];
                      const deltaX = touch.clientX - touchStart.x;
                      const moved = Math.abs(deltaX) > 10;
                      
                      if (moved && deltaX < -50) {
                        setSwipedMatch(match.id);
                        setTouchStart(null);
                      }
                    }}
                    onTouchEnd={(e) => {
                      if (touchStart && touchStart.matchId === match.id) {
                        const duration = Date.now() - touchStart.startTime;
                        const moved = Math.abs(e.changedTouches[0].clientX - touchStart.x) < 10;
                        
                        // If it was a quick tap (not a swipe), check for double tap
                        if (duration < 300 && moved) {
                          handleDoubleTap(match.id);
                        }
                      }
                      setTouchStart(null);
                    }}
                    onMouseDown={(e) => {
                      // Prevent if clicking on buttons
                      if (e.target.closest('button')) return;
                      setTouchStart({ x: e.clientX, matchId: match.id });
                    }}
                    onMouseMove={(e) => {
                      if (!touchStart || touchStart.matchId !== match.id) return;
                      const deltaX = e.clientX - touchStart.x;
                      if (deltaX < -50) {
                        setSwipedMatch(match.id);
                        setTouchStart(null);
                      }
                    }}
                    onMouseUp={() => {
                      setTouchStart(null);
                    }}
                    onMouseLeave={() => {
                      setTouchStart(null);
                    }}
                  >
                    {/* Favorite Badge */}
                    {favoritedMatches.includes(match.id) && (
                      <div className="absolute top-2 right-2 z-10 bg-yellow-500 text-white rounded-full p-2 shadow-lg">
                        <Star className="w-4 h-4 fill-current" />
                      </div>
                    )}

                    {/* User Profile Header */}
                    <div className="flex items-center space-x-4 mb-4">
                      {match.user.profile_image ? (
                        <img
                          src={resolveImageUrl(match.user.profile_image)}
                          alt={getDisplayName(match.user.full_name)}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-xl">
                            {match.user.full_name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-emerald-800 dark:text-emerald-100 text-lg">
                            {getDisplayName(match.user.full_name)}
                          </h3>
                          
                          {/* Three-Dot Menu */}
                          <div className="relative" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuMatchId(openMenuMatchId === match.id ? null : match.id);
                              }}
                              className="p-1 hover:bg-emerald-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            </button>
                            
                            {openMenuMatchId === match.id && (
                              <>
                                <div 
                                  className="fixed inset-0 z-10" 
                                  onClick={() => setOpenMenuMatchId(null)}
                                />
                                
                                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-emerald-200 dark:border-slate-700 rounded-lg shadow-xl z-20">
                                  {/* Hide Match */}
                                  <button
                                    onClick={() => {
                                      handleHideUser(match.id, match.user.full_name);
                                      setOpenMenuMatchId(null);
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-emerald-50 dark:hover:bg-slate-700 flex items-center space-x-3 transition-colors"
                                  >
                                    <EyeOff className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                    <span className="text-emerald-800 dark:text-emerald-100 font-medium">Hide</span>
                                  </button>

                                  {/* Delete Match */}
                                  <button
                                    onClick={() => {
                                      setMatchToDelete(match);
                                      setShowDeleteModal(true);
                                      setOpenMenuMatchId(null);
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-3 transition-colors border-t border-emerald-100 dark:border-slate-700"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                    <span className="text-red-600 font-medium">Delete</span>
                                  </button>

                                  {/* Block User */}
                                  <button
                                    onClick={() => {
                                      setMatchToBlock(match);
                                      setShowBlockModal(true);
                                      setOpenMenuMatchId(null);
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-3 transition-colors border-t border-red-100 dark:border-red-900/30"
                                  >
                                    <Ban className="w-4 h-4 text-red-600" />
                                    <span className="text-red-600 font-medium">Block</span>
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-emerald-600 dark:text-emerald-300">
                          {match.user.age} • {match.user.location}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                            <Heart className="w-3 h-3 text-white fill-current" />
                          </div>
                          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                            {Math.round(match.compatibility_score)}% Match
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Bio */}
                    {match.user.bio && (
                      <div className="mb-4">
                        <p className="text-sm text-emerald-700 dark:text-emerald-200 line-clamp-3">
                          {match.user.bio}
                        </p>
                      </div>
                    )}

                    {/* Mental Health Goals & Interests */}
                    {(match.user.mental_health_goals?.length > 0 || match.user.interests_hobbies?.length > 0) && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {match.user.mental_health_goals?.slice(0, 2).map(goal => (
                            <span key={goal} className="px-2 py-1 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 text-xs rounded-full">
                              {goal}
                            </span>
                          ))}
                          {match.user.interests_hobbies?.slice(0, 2).map(interest => (
                            <span key={interest} className="px-2 py-1 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 text-xs rounded-full">
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Connection Points */}
                    {match.match_reasons && match.match_reasons.length > 0 && (
                      <div className="mb-4 p-3 bg-emerald-50 dark:bg-slate-800 rounded-lg">
                        <h4 className="text-sm font-medium text-emerald-800 dark:text-emerald-100 mb-2 flex items-center">
                          <Sparkles className="w-4 h-4 mr-1" />
                          Connection Points
                        </h4>
                        <div className="space-y-1">
                          {match.match_reasons.slice(0, 2).map((reason, idx) => (
                            <div key={idx} className="flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                              <span className="text-xs text-emerald-600 dark:text-emerald-300">{reason}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="space-y-2">
                      <button 
                        data-action-button="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(match.id, match.user.full_name);
                        }}
                        className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg ${
                          favoritedMatches.includes(match.id)
                            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700'
                            : 'bg-white dark:bg-slate-700 text-yellow-600 dark:text-yellow-400 border-2 border-yellow-500 hover:bg-yellow-50 dark:hover:bg-slate-600'
                        }`}
                      >
                        <Star className={`w-4 h-4 ${favoritedMatches.includes(match.id) ? 'fill-current' : ''}`} />
                        <span>{favoritedMatches.includes(match.id) ? 'Favorited' : 'Add to Favorites'}</span>
                      </button>
                      
                      <button 
                        data-action-button="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartConversation(match.user.id, match.user.full_name);
                        }}
                        className="btn-primary w-full flex items-center justify-center space-x-2"
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>Send Message</span>
                      </button>
                      
                      <button 
                        data-action-button="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProfile(match);
                        }}
                        className="btn-secondary w-full flex items-center justify-center space-x-2"
                      >
                        <User className="w-4 h-4" />
                        <span>View Profile</span>
                      </button>

                      <button 
                        data-action-button="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDatePlannerMatch(match.user);
                          setShowDatePlanner(true);
                        }}
                        className="w-full flex items-center justify-center space-x-2 py-2 px-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 transition-colors text-sm font-medium"
                        data-testid="plan-date-matches-btn"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>Plan a Date</span>
                      </button>
                    </div>

                    {/* Match Date */}
                    <div className="mt-3 text-xs text-emerald-500 dark:text-emerald-400 text-center">
                      Matched {new Date(match.created_at).toLocaleDateString('en-AU', { 
                        month: 'short', 
                        day: 'numeric'
                      })}
                    </div>

                    {/* Swipe/Double-Tap Action Buttons */}
                    {isSwiped && (
                      <div className="absolute right-0 top-0 bottom-0 flex flex-col items-center justify-center space-y-2 pr-2 z-10 animate-fade-in">
                        <div className="bg-white dark:bg-slate-700 rounded-lg px-2 py-1 mb-1 shadow-md">
                          <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Actions</p>
                        </div>
                        
                        <button
                          data-action-button="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFavorite(match.id, match.user.full_name);
                          }}
                          className={`p-3 ${
                            favoritedMatches.includes(match.id)
                              ? 'bg-gradient-to-br from-yellow-500 to-yellow-600'
                              : 'bg-gradient-to-br from-yellow-400 to-yellow-500'
                          } text-white rounded-full shadow-lg hover:scale-110 transition-all transform`}
                          title={favoritedMatches.includes(match.id) ? "Remove from Favorites" : "Add to Favorites"}
                        >
                          <Star className={`w-5 h-5 ${favoritedMatches.includes(match.id) ? 'fill-current' : ''}`} />
                        </button>
                        
                        <button
                          data-action-button="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleHideUser(match.id, match.user.full_name);
                          }}
                          className="p-3 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full shadow-lg hover:from-amber-600 hover:to-amber-700 transition-all transform hover:scale-110"
                          title="Hide User"
                        >
                          <EyeOff className="w-5 h-5" />
                        </button>
                        
                        <button
                          data-action-button="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMatchToDelete(match);
                            setShowDeleteModal(true);
                          }}
                          className="p-3 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full shadow-lg hover:from-red-600 hover:to-red-700 transition-all transform hover:scale-110"
                          title="Delete Match"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        
                        <button
                          data-action-button="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMatchToBlock(match);
                            setShowBlockModal(true);
                          }}
                          className="p-3 bg-gradient-to-br from-gray-700 to-gray-800 text-white rounded-full shadow-lg hover:from-gray-800 hover:to-gray-900 transition-all transform hover:scale-110"
                          title="Block User"
                        >
                          <Ban className="w-5 h-5" />
                        </button>
                      </div>
                    )}

                    {/* Close swipe overlay */}
                    {isSwiped && (
                      <div 
                        className="fixed inset-0 z-0"
                        onClick={() => setSwipedMatch(null)}
                      />
                    )}
                  </div>
                  );
                })}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="card max-w-md mx-auto">
                  <Heart className="w-16 h-16 text-emerald-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-emerald-800 mb-2">No Active Matches Yet</h3>
                  <p className="text-emerald-600 mb-6">
                    Start connecting with people in the Discover tab to build your matches!
                  </p>
                  <button
                    onClick={() => setActiveTab('suggestions')}
                    className="btn-primary flex items-center space-x-2 mx-auto"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Discover People</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Profile Viewing Modal */}
        {selectedProfile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="relative">
                {/* Close Button */}
                <button
                  onClick={() => setSelectedProfile(null)}
                  className="absolute top-4 right-4 w-8 h-8 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-full flex items-center justify-center transition-colors z-10"
                >
                  <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </button>

                {/* Profile Header */}
                <div className="relative h-64 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-800 dark:to-teal-800 rounded-t-2xl">
                  {selectedProfile.user.profile_image ? (
                    <img
                      src={resolveImageUrl(selectedProfile.user.profile_image)}
                      alt={getDisplayName(selectedProfile.user.full_name)}
                      className="w-full h-full object-cover rounded-t-2xl"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-24 h-24 text-emerald-400 dark:text-emerald-300" />
                    </div>
                  )}
                  
                  {/* Compatibility Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white dark:bg-slate-800 rounded-full shadow-lg">
                    <div className="flex items-center space-x-2">
                      <Heart className="w-4 h-4 text-pink-500 fill-current" />
                      <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                        {Math.round(selectedProfile.compatibility_score)}% Match
                      </span>
                    </div>
                  </div>
                </div>

                {/* Profile Content */}
                <div className="p-6 space-y-6">
                  {/* Name and Basic Info */}
                  <div>
                    <h2 className="text-3xl font-bold text-emerald-800 dark:text-emerald-100 mb-2">
                      {getDisplayName(selectedProfile.user.full_name)}
                    </h2>
                    <div className="flex items-center space-x-4 text-emerald-600 dark:text-emerald-300">
                      <span>{selectedProfile.user.age} years old</span>
                      <span>•</span>
                      <span>{selectedProfile.user.location}</span>
                    </div>
                  </div>

                  {/* Bio */}
                  {selectedProfile.user.bio && (
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-2">About</h3>
                      <p className="text-emerald-700 dark:text-emerald-200 leading-relaxed">
                        {selectedProfile.user.bio}
                      </p>
                    </div>
                  )}

                  {/* Mental Health Goals */}
                  {selectedProfile.user.mental_health_goals?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-3">
                        Mental Health Goals
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.user.mental_health_goals.map(goal => (
                          <span key={goal} className="px-3 py-2 bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-200 rounded-full text-sm">
                            {goal}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interests & Hobbies */}
                  {selectedProfile.user.interests_hobbies?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-3">
                        Interests & Hobbies
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.user.interests_hobbies.map(interest => (
                          <span key={interest} className="px-3 py-2 bg-teal-100 dark:bg-teal-800 text-teal-700 dark:text-teal-200 rounded-full text-sm">
                            {interest}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Relationship Info */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-emerald-50 dark:bg-slate-800 rounded-xl">
                    <div>
                      <h4 className="text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-1">
                        Relationship Status
                      </h4>
                      <p className="text-emerald-800 dark:text-emerald-100">
                        {selectedProfile.user.relationship_status || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-emerald-600 dark:text-emerald-400 mb-1">
                        Looking For
                      </h4>
                      <p className="text-emerald-800 dark:text-emerald-100">
                        {selectedProfile.user.looking_for || 'Not specified'}
                      </p>
                    </div>
                  </div>

                  {/* Connection Points */}
                  {selectedProfile.match_reasons?.length > 0 && (
                    <div className="p-4 bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-xl">
                      <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-100 mb-3 flex items-center">
                        <Sparkles className="w-5 h-5 mr-2" />
                        Why You're Compatible
                      </h3>
                      <div className="space-y-2">
                        {selectedProfile.match_reasons.map((reason, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                            <span className="text-emerald-700 dark:text-emerald-200">{reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4 border-t border-emerald-200 dark:border-slate-600">
                    <button 
                      onClick={() => {
                        handleStartConversation(selectedProfile.user.id, selectedProfile.user.full_name);
                        setSelectedProfile(null);
                      }}
                      className="flex-1 btn-primary flex items-center justify-center space-x-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>Send Message</span>
                    </button>
                    
                    <button 
                      onClick={() => setSelectedProfile(null)}
                      className="px-6 btn-secondary"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Match Confirmation Modal */}
        {showDeleteModal && matchToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Match?</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300">
                  Are you sure you want to delete your match with <strong>{getDisplayName(matchToDelete.user.full_name)}</strong>? 
                  You will no longer be able to message each other, and they will be removed from your matches list.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMatchToDelete(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteMatch}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-colors font-bold"
                >
                  Delete Match
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Block User Confirmation Modal */}
        {showBlockModal && matchToBlock && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-md w-full shadow-2xl">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-900/30 rounded-full flex items-center justify-center">
                  <Ban className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Block User?</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This is a serious action</p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Are you sure you want to block <strong>{getDisplayName(matchToBlock.user.full_name)}</strong>?
                </p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>They won't be able to message you</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>They won't see your profile in suggestions</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">•</span>
                    <span>Your match will be permanently removed</span>
                  </li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setMatchToBlock(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBlockUser}
                  className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-xl hover:bg-gray-800 transition-colors font-bold"
                >
                  Block User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Date Planning Assistant Modal */}
        {showDatePlanner && datePlannerMatch && (
          <DatePlanningAssistant
            matchUserId={datePlannerMatch.id}
            matchName={getDisplayName(datePlannerMatch.full_name)}
            onClose={() => {
              setShowDatePlanner(false);
              setDatePlannerMatch(null);
            }}
            isModal={true}
          />
        )}
      </div>
    </div>
  );
};

export default Matches;